import { useState, useCallback } from 'react';
import { addFilter, checkFilterName } from '@opm/services';
import { getPagePath, PAGE_ENUM_MAP } from '../utils';

/**
 * 筛选器数据项类型
 */
export interface FilterItem {
  filter_id: number;
  filter_name: string;
  filter_info: string;
  system?: string;
}

/**
 * useHistoryFilterMutation Hook 参数
 */
export interface UseHistoryFilterMutationProps {
  /** 添加成功后的回调 */
  onAddSuccess?: (filterName: string) => void;
  /** 添加失败后的回调 */
  onAddError?: (error: any) => void;
  pagePath?: PAGE_ENUM_MAP;
}

/**
 * useHistoryFilterMutation Hook 返回值
 */
export interface UseHistoryFilterMutationReturn {
  /** 加载状态 */
  loading: boolean;
  /** 添加筛选器 */
  addFilterItem: (filterName: string, filterInfo: any) => Promise<boolean>;
  /** 检查筛选器名称是否存在 */
  checkNameExists: (filterName: string) => Promise<boolean>;
}

/**
 * 历史筛选器增加和检查名称 Hook
 * 封装筛选器的添加、名称检查操作
 *
 * @param props - 配置参数
 * @returns Hook 返回值
 *
 * @example
 * ```tsx
 * const {
 *   loading,
 *   addFilterItem,
 *   checkNameExists
 * } = useHistoryFilterMutation({
 *   onAddSuccess: (name) => message.success(`筛选器 ${name} 已保存`)
 * });
 *
 * // 检查名称
 * const exists = await checkNameExists('我的筛选器');
 *
 * // 添加筛选器
 * await addFilterItem('我的筛选器', { channel: ['YouTube'] });
 * ```
 */
export function useHistoryFilterMutation(
  props: UseHistoryFilterMutationProps = {},
): UseHistoryFilterMutationReturn {
  const { onAddSuccess, onAddError, pagePath } = props;

  const [loading, setLoading] = useState(false);

  /**
   * 检查筛选器名称是否存在
   */
  const checkNameExists = useCallback(
    async (filterName: string): Promise<boolean> => {
      try {
        const data = await checkFilterName({
          filter_name: filterName,
          page_path: getPagePath(pagePath),
          business_type: 1,
        });
        return data?.exists ?? false;
      } catch (error) {
        console.error('Failed to check filter name:', error);
        return true;
      }
    },
    [],
  );

  /**
   * 添加筛选器
   */
  const addFilterItem = async (
    filterName: string,
    filterInfo: any,
  ): Promise<boolean> => {
    setLoading(true);
    try {
      const condInfo = JSON.stringify(filterInfo);
      const data = await addFilter({
        filter_name: filterName,
        filter_cond: condInfo,
        page_path: getPagePath(pagePath),
        business_type: 1,
      });
      onAddSuccess?.({
        ...data,
        filter_name: filterName,
        filter_cond: condInfo,
      });
      return true;
    } catch (error) {
      console.error('Failed to add filter:', error);
      onAddError?.(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    addFilterItem,
    checkNameExists,
  };
}
